// Turns the game's real data (your answers, the room's split, the leaderboard) into the inputs the kudos picker needs.
// Pure functions, unit-tested; the copy itself lives in lib/kudos.ts.
import type { Highlights, LeaderboardRow, MeAnswer, MeResponse, Reveal } from '@shared/types';
import { HIGHLIGHTS, fill, ordinal, podiumLine, type BoardContext, type RevealContext } from '@/lib/kudos';
import type { MyAnswer } from '@/store/game';

/**
 * Answers as GET /me returns them. While a question is open its answer comes back without `correct` and `points`
 * (and the score, streak and stats stand as they were before it), so correctness is never read from an open answer.
 */
export type Answers = Record<string, MeAnswer>;

/**
 * The server's streak moves only when a player answers: a missed question leaves it as it was.
 * The copy follows the server so the flame never disagrees with the points. Flip this if the server starts breaking streaks on a miss.
 */
export const MISS_BREAKS_STREAK = false;

/** An answer to a question that has closed: the server has said whether it was right. */
export const isSettled = (a: MeAnswer | undefined): a is MeAnswer & { correct: boolean } => Boolean(a) && typeof a!.correct === 'boolean';

const validIndex = (n: number) => Number.isInteger(n) && n >= 0;
const entries = (answers: Answers) =>
  Object.entries(answers ?? {})
    .map(([k, a]) => ({ index: Number(k.slice(1)), a }))
    .filter((e) => validIndex(e.index) && Boolean(e.a))
    .sort((x, y) => x.index - y.index);
/** Only the answers whose question has closed. */
const settled = (answers: Answers) => entries(answers).filter((e): e is { index: number; a: MeAnswer & { correct: boolean } } => isSettled(e.a));

/** Streak and run of misses going into question `index`. Questions before a late joiner's first answer do not count. */
export function historyBefore(answers: Answers, index: number): { streak: number; wrongRun: number } {
  const list = entries(answers); const first = list[0]?.index;
  let streak = 0; let wrongRun = 0;
  if (first === undefined) return { streak, wrongRun };
  for (let i = first; i < index; i++) {
    const a = answers[`q${i}`];
    if (!a) { wrongRun++; if (MISS_BREAKS_STREAK) streak = 0; continue; }
    if (!isSettled(a)) continue; // still open when /me was read: it says nothing yet
    if (a.correct) { streak++; wrongRun = 0; } else { streak = 0; wrongRun++; }
  }
  return { streak, wrongRun };
}

export function bestStreakOf(answers: Answers): number {
  let best = 0; let run = 0; let prev: number | undefined;
  for (const { index, a } of settled(answers)) {
    if (MISS_BREAKS_STREAK && prev !== undefined && index !== prev + 1) run = 0;
    run = a.correct ? run + 1 : 0; best = Math.max(best, run); prev = index;
  }
  return best;
}
export function fastestCorrectMs(answers: Answers): number | undefined {
  const t = settled(answers).filter((e) => e.a.correct).map((e) => e.a.elapsedMs);
  return t.length ? Math.min(...t) : undefined;
}
export const correctCountOf = (answers: Answers) => settled(answers).filter((e) => e.a.correct).length;
/** Answers to closed questions (an open question's answer is not counted until it closes). */
export const answeredCountOf = (answers: Answers) => settled(answers).length;

/** The parts of a reveal that say what was right, for every format (v2.2: several right regions or options, credit buckets). */
export type RevealKey = Pick<Reveal, 'correctId'> & Partial<Pick<Reveal, 'correctIds' | 'credit' | 'correctOrder' | 'numberAnswer'>>;

/**
 * Credit buckets (v2.2): for number, order and multi 'all' the answer's optionId is 'full', 'partial' or 'none', and only
 * full credit is right (it keeps the streak); partial credit still scores some points.
 */
export type Credit = 'full' | 'partial' | 'none';
export const isCredit = (id: string | undefined): id is Credit => id === 'full' || id === 'partial' || id === 'none';
/** A reveal of a format the server grades in buckets: it carries the credit split, the true order or the true value. */
export const isBucketReveal = (r: RevealKey) => r.credit !== undefined || r.correctOrder !== undefined || r.numberAnswer !== undefined;
/** Whether a pick is right on this reveal: the right option, any of the right regions or options, or full credit. */
export function isRightPick(optionId: string, r: RevealKey): boolean {
  if (isCredit(optionId) || isBucketReveal(r)) return optionId === 'full';
  return optionId === r.correctId || Boolean(r.correctIds?.includes(optionId));
}

/**
 * Share of the room that got it right, 0–100, or undefined when nobody answered. Number, order and multi 'all' count full
 * credit; hotspot and multi rarity count everyone who picked any right region or option.
 */
export function roomCorrectPct(r: Pick<Reveal, 'counts' | 'answered'> & RevealKey): number | undefined {
  if (!(r.answered > 0)) return undefined;
  if (r.credit) return Math.min(100, (100 * (r.credit.full ?? 0)) / r.answered);
  const ids = r.correctIds?.length ? [...new Set(r.correctIds)] : [r.correctId];
  return Math.min(100, (100 * ids.reduce((n, id) => n + (r.counts?.[id] ?? 0), 0)) / r.answered);
}

/** True when `me` was read after question `index` closed, so its answers are the full story for that question. */
export function meCovers(me: MeResponse | undefined, playerId: string | undefined, index: number): boolean {
  if (!me || !playerId || me.playerId !== playerId) return false;
  const s = me.state; if (!s) return false;
  return s.index > index || (s.index === index && s.status !== 'question' && s.status !== 'lobby');
}

/** `credit` is set for the formats graded in buckets: 'partial' is a wrong answer that still scored some points. */
export type Answered = { kind: 'right' | 'wrong'; optionId: string; elapsedMs?: number; points?: number; server: boolean; credit?: Credit };
export type MyResult =
  | { kind: 'pending' }                       // not known yet (a reload during the reveal, before /me answers)
  | { kind: 'none' }                          // no answer from this player
  | { kind: 'lost'; optionId: string }         // locked in here, but the server never recorded it
  | Answered;
export const isAnswered = (r: MyResult): r is Answered => r.kind === 'right' || r.kind === 'wrong';

const creditOf = (optionId: string): { credit?: Credit } => (isCredit(optionId) ? { credit: optionId } : {});

/** What happened to this player on question `index`: the server's record wins, then this phone's lock-in. */
export function myResultFor(p: { index: number; reveal: RevealKey; startsAt?: number; myAnswer?: MyAnswer; me?: MeResponse; playerId?: string; seenQuestion?: number }): MyResult {
  const mine = p.me && p.playerId && p.me.playerId === p.playerId ? p.me : undefined;
  const rec = mine?.answers?.[`q${p.index}`];
  if (rec && isSettled(rec)) return { kind: rec.correct ? 'right' : 'wrong', optionId: rec.optionId, elapsedMs: rec.elapsedMs, points: rec.points, server: true, ...creditOf(rec.optionId) };
  // Read while the question was still open, the record holds the choice and its time but no verdict. The reveal says
  // whether that choice was right; the points follow with the next /me.
  if (rec) return { kind: isRightPick(rec.optionId, p.reveal) ? 'right' : 'wrong', optionId: rec.optionId, elapsedMs: rec.elapsedMs, server: false, ...creditOf(rec.optionId) };
  const covered = meCovers(p.me, p.playerId, p.index);
  const local = p.myAnswer && p.myAnswer.index === p.index ? p.myAnswer : undefined;
  if (local) {
    if (covered) return { kind: 'lost', optionId: local.optionId };
    // Number, order and multi 'all' are graded by the server: this phone waits for its verdict (the next /me) rather than guess.
    if (isBucketReveal(p.reveal) && !isCredit(local.optionId)) return { kind: 'pending' };
    const elapsedMs = local.elapsedMs ?? (p.startsAt !== undefined ? Math.max(0, local.lockedAt - p.startsAt) : undefined);
    return { kind: isRightPick(local.optionId, p.reveal) ? 'right' : 'wrong', optionId: local.optionId, elapsedMs, server: false, ...creditOf(local.optionId) };
  }
  if (covered || p.seenQuestion === p.index) return { kind: 'none' };
  return { kind: 'pending' };
}

/** The kudos picker's view of this reveal. */
export function revealContextFor(p: { playerId: string; index: number; result: MyResult; durationMs: number; answers?: Answers; reveal: Pick<Reveal, 'counts' | 'answered'> & RevealKey; me?: MeResponse }): RevealContext {
  const { streak, wrongRun } = historyBefore(p.answers ?? {}, p.index);
  const r = p.result; const answered = isAnswered(r); const correct = r.kind === 'right';
  // Once /me was read at this question's reveal its streak is the server's own number.
  const serverStreak = correct && r.server && readAtReveal(p.me, p.playerId, p.index) ? p.me!.streak : undefined;
  return {
    playerId: p.playerId, questionIndex: p.index, answered, correct,
    elapsedMs: answered ? r.elapsedMs : undefined, durationMs: Math.max(1, p.durationMs),
    streakBefore: !answered && !MISS_BREAKS_STREAK ? 0 : streak,
    streakAfter: correct ? (serverStreak ?? streak + 1) : answered ? 0 : streak,
    wrongRunBefore: wrongRun,
    roomCorrectPct: roomCorrectPct(p.reveal),
  };
}

/**
 * True when `me` was read once question `index` had closed and before the next one opened, so its score, streak and
 * ranks belong to this question's leaderboard. A /me read while the question was open still has the figures from before it.
 */
export function readAtReveal(me: MeResponse | undefined, playerId: string | undefined, index: number): boolean {
  return Boolean(me && playerId && me.playerId === playerId && me.state?.index === index && meCovers(me, playerId, index));
}

/**
 * GET /me may also carry the score of the place directly above (`scoreAbove`, with any rarity points). The server does
 * not send it yet; once it does, players ranked 12th or lower get their exact gap without a change here.
 */
export type MeWithAbove = MeResponse & { scoreAbove?: number };

/**
 * What a player outside the top ten is chasing: the place directly above when its score is known (`exact`), otherwise
 * the last place on the board (10th: the way into the top ten). `gap` is in points; 0 means level, behind on time.
 */
export interface Chase { place: number; gap: number; exact: boolean }

/**
 * The leaderboard line's inputs: your rank and movement from the top ten or /me, and the gap to the place above when it
 * is known (from the board, or from /me's score of the place above). Outside the top ten, `chase` is the gap to show.
 */
export function boardContextFor(p: { playerId: string; index: number; top: LeaderboardRow[]; me?: MeResponse; players: number }): BoardContext & { score?: number; streak?: number; inTop: boolean; chase?: Chase } {
  const row = p.top.find((r) => r.playerId === p.playerId);
  const fresh: MeWithAbove | undefined = readAtReveal(p.me, p.playerId, p.index) ? p.me : undefined;
  const rank = row?.rank ?? fresh?.rank;
  const prevRank = row?.prevRank ?? fresh?.prevRank;
  const score = row?.score ?? fresh?.score;
  const above = rank && rank > 1 ? p.top.find((r) => r.rank === rank - 1) : undefined;
  const aboveScore = above?.score ?? (!row && rank && rank > 1 && typeof fresh?.scoreAbove === 'number' && Number.isFinite(fresh.scoreAbove) ? fresh.scoreAbove : undefined);
  const gapToAbove = aboveScore !== undefined && score !== undefined ? Math.max(0, aboveScore - score) : undefined;
  let chase: Chase | undefined;
  if (!row && rank && rank > 1 && score !== undefined) {
    if (gapToAbove !== undefined) chase = { place: rank - 1, gap: gapToAbove, exact: true };
    else {
      const last = p.top.filter((r) => r.rank < rank).sort((x, y) => y.rank - x.rank)[0];
      if (last) chase = { place: last.rank, gap: Math.max(0, last.score - score), exact: false };
    }
  }
  return { playerId: p.playerId, questionIndex: p.index, rank, prevRank, players: p.players, gapToAbove, nameAbove: above?.name, score, streak: row?.streak ?? fresh?.streak, inTop: Boolean(row), chase };
}

export type HighlightKey = 'climber' | 'fastest' | 'streak' | 'photo';
export interface HighlightItem { key: HighlightKey; text: string; mine: boolean }
/**
 * The highlights strip under the leaderboard, in a fixed order. Each fact once per screen: when the climber's or the
 * streak holder's row is on the board (`onBoard`), its movement badge or flame already shows the number, so the chip
 * names them only; and when the photo finish is my race with the place above me (`myRank`), my own line says so.
 */
export function highlightItems(h: Highlights | undefined, myId?: string, opts: { onBoard?: Iterable<string>; myRank?: number } = {}): HighlightItem[] {
  if (!h) return [];
  const shown = new Set(opts.onBoard ?? []);
  const out: HighlightItem[] = [];
  if (h.climber && h.climber.delta > 0) {
    const text = shown.has(h.climber.playerId) ? fill(HIGHLIGHTS.climberName, { name: h.climber.name }) : fill(HIGHLIGHTS.climber, { name: h.climber.name, n: h.climber.delta });
    out.push({ key: 'climber', text, mine: h.climber.playerId === myId });
  }
  if (h.fastest) out.push({ key: 'fastest', text: fill(HIGHLIGHTS.fastest, { name: h.fastest.name, s: (h.fastest.elapsedMs / 1000).toFixed(1) }), mine: h.fastest.playerId === myId });
  if (h.streak && h.streak.streak >= 2) {
    const text = shown.has(h.streak.playerId) ? fill(HIGHLIGHTS.streakName, { name: h.streak.name }) : fill(HIGHLIGHTS.streak, { name: h.streak.name, n: h.streak.streak });
    out.push({ key: 'streak', text, mine: h.streak.playerId === myId });
  }
  if (h.photo && !(opts.myRank !== undefined && h.photo.place === opts.myRank - 1)) out.push({ key: 'photo', text: fill(HIGHLIGHTS.photo, { place: ordinal(h.photo.place), gap: h.photo.gap }), mine: false });
  return out;
}

export interface ResultSummary {
  rank?: number; players: number; score: number; correct: number; answered: number; asked: number;
  accuracy?: number; bestStreak: number; fastestMs?: number; line?: string;
}
/** Your result card at the podium and on the result page: the server's v2 figures when present, otherwise worked out from your answers. */
export function resultSummary(me: MeResponse): ResultSummary {
  const answers = me.answers ?? {};
  const correct = me.correctCount ?? correctCountOf(answers);
  const answered = answeredCountOf(answers);
  // Questions asked so far: an open question is not counted until it closes.
  const s = me.state; const closedThrough = !s ? -1 : s.status === 'question' ? s.index - 1 : s.index;
  const asked = Math.max(answered, closedThrough + 1, 0);
  return {
    rank: me.rank, players: me.players, score: me.score ?? 0, correct, answered, asked,
    accuracy: answered > 0 ? Math.round((100 * correct) / answered) : undefined,
    bestStreak: me.bestStreak ?? bestStreakOf(answers),
    fastestMs: me.fastestMs ?? fastestCorrectMs(answers),
    line: me.rank ? podiumLine(me.rank, Math.max(me.players, me.rank), `${me.playerId}:podium`) : undefined,
  };
}

export const seconds = (ms: number) => (ms / 1000).toFixed(1);
